import { useContext, useCallback, useEffect, useRef, useState } from 'react';
import SSEContext from '../context/SSEContext';

/**
 * Hook to interact with the SSE context.
 *
 * Basic usage — subscribe to events:
 *   const { subscribe, connectToSource, connectionStatus } = useSSE();
 *
 * Workflow-specific convenience:
 *   const { status, nodes } = useWorkflowSSE(workflowId);
 *
 * Balance-specific convenience:
 *   const { balance } = useBalanceSSE();
 */
export function useSSE() {
  const ctx = useContext(SSEContext);
  if (!ctx) {
    throw new Error('useSSE must be used within an SSEProvider');
  }
  return ctx;
}

/**
 * Subscribe to workflow status events for a specific workflow.
 *
 * Manages the EventSource connection lifecycle: opens on mount / when
 * workflowId changes, closes on unmount or when workflowId is null.
 */
export function useWorkflowSSE(workflowId) {
  const { subscribe, connectToSource, disconnectFromSource, connectionStatus } = useSSE();
  const [status, setStatus] = useState(null);
  const [nodes, setNodes] = useState([]);
  const [progressPct, setProgressPct] = useState(0);
  const [error, setError] = useState(null);

  const urlRef = useRef(null);

  // Connect / disconnect when workflowId changes
  useEffect(() => {
    if (!workflowId) return;

    const url = `/api/v1/workflows/${workflowId}/events`;
    urlRef.current = url;
    connectToSource(url);

    return () => {
      disconnectFromSource(url);
      urlRef.current = null;
    };
  }, [workflowId, connectToSource, disconnectFromSource]);

  // Subscribe to events
  useEffect(() => {
    if (!workflowId) return;

    const unsubs = [];

    unsubs.push(subscribe('snapshot', (data) => {
      if (data.workflow_id !== String(workflowId)) return;
      setStatus(data.status);
      setNodes(data.nodes || []);
      setProgressPct(data.progress_pct || 0);
    }));

    unsubs.push(subscribe('workflow.node.started', (data) => {
      if (data.workflow_id !== String(workflowId)) return;
      setNodes(prev => prev.map(n =>
        n.id === data.node_id ? { ...n, status: 'running' } : n
      ));
      if (data.progress_pct != null) setProgressPct(data.progress_pct);
    }));

    unsubs.push(subscribe('workflow.node.completed', (data) => {
      if (data.workflow_id !== String(workflowId)) return;
      setNodes(prev => prev.map(n =>
        n.id === data.node_id ? { ...n, status: 'completed', cost: data.cost } : n
      ));
      if (data.progress_pct != null) setProgressPct(data.progress_pct);
    }));

    unsubs.push(subscribe('workflow.node.failed', (data) => {
      if (data.workflow_id !== String(workflowId)) return;
      setNodes(prev => prev.map(n =>
        n.id === data.node_id ? { ...n, status: 'failed', error: data.error } : n
      ));
      if (data.progress_pct != null) setProgressPct(data.progress_pct);
    }));

    unsubs.push(subscribe('workflow.completed', (data) => {
      if (data.workflow_id !== String(workflowId)) return;
      setStatus('completed');
      if (data.progress_pct != null) setProgressPct(data.progress_pct);
    }));

    unsubs.push(subscribe('workflow.failed', (data) => {
      if (data.workflow_id !== String(workflowId)) return;
      setStatus('failed');
      setError(data.error || 'Workflow failed');
    }));

    return () => unsubs.forEach(fn => fn());
  }, [workflowId, subscribe]);

  return { status, nodes, progressPct, error, connectionStatus };
}

/**
 * Subscribe to real-time balance updates for the authenticated user.
 *
 * Opens the balance SSE stream on mount, closes on unmount.
 */
export function useBalanceSSE() {
  const { subscribe, connectToSource, disconnectFromSource, connectionStatus } = useSSE();
  const [balance, setBalance] = useState(null);

  const url = '/api/v1/balance/events';

  useEffect(() => {
    connectToSource(url);
    return () => disconnectFromSource(url);
  }, [connectToSource, disconnectFromSource]);

  // Apply snapshot and incremental updates
  useEffect(() => {
    const applyBalance = (data) => {
      // Prefer credit_balance fields (org wallet); fall back to legacy current_balance
      const creditBalance = data.credit_balance != null
        ? parseFloat(data.credit_balance)
        : parseFloat(data.current_balance || 0);
      const creditReserved = data.credit_reserved != null
        ? parseFloat(data.credit_reserved)
        : parseFloat(data.reserved_amount || 0);
      const available = data.available_credits != null
        ? parseFloat(data.available_credits)
        : parseFloat(data.available_balance || 0);

      setBalance({
        // Credits (integer, 100 = £1)
        creditBalance,
        creditReserved,
        availableCredits: available,
        // GBP equivalents for display (divide by 100)
        current: creditBalance / 100,
        reserved: creditReserved / 100,
        available: available / 100,
        currency: data.currency || 'credits',
        orgId: data.org_id,
        userId: data.user_id,
      });
    };

    const unsubs = [
      subscribe('snapshot', (data) => {
        // Only handle balance snapshots (has credit_balance or current_balance field)
        if (data.credit_balance != null || data.current_balance != null) applyBalance(data);
      }),
      subscribe('balance.reserved', applyBalance),
      subscribe('balance.consumed', applyBalance),
      subscribe('balance.released', applyBalance),
      subscribe('balance.finalized', applyBalance),
      subscribe('balance.topup', applyBalance),
    ];

    return () => unsubs.forEach(fn => fn());
  }, [subscribe]);

  return { balance, connectionStatus };
}
